import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Select,
  MultiSelect,
  TextInput,
  NumberInput,
  Group,
  Button,
  Text,
} from "@mantine/core";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { toast } from "react-toastify";
import { useAppSelector } from "../../hooks/hooks";
import { title } from "process";

export interface RewardTemplate {
  id: string;
  name: string;
  reward_category: string;
  value: number;
  product_name?: string;
  product_id?: any;
  allowed_class_id?: string;
  allowed_class_name?: string;
  target_package_id?: string;
  target_package_name?: string;
  expiry_days: number;
  created_at?: any;
}

interface RewardModalProps {
  opened: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  editingReward?: RewardTemplate | null;
}

export default function RewardModal({
  opened,
  onClose,
  onSaveSuccess,
  editingReward,
}: RewardModalProps) {
  const companyId = useAppSelector((state: any) => state.session?.company_id);
  const { branchesList } = useAppSelector((state: any) => state.branchList);

  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [products, setProducts] = useState<{ value: string; label: string }[]>(
    [],
  );

  const [formData, setFormData] = useState({
    name: "",
    reward_category: "membership_extension",
    value: 0,
    product_name: "",
    product_id: "",
    allowed_class_id: "",
    allowed_class_name: "",
    target_package_id: "",
    target_package_name: "",
    expiry_days: 0,
    branch_ids: [] as string[],
  });

  const [packages, setPackages] = useState<
    { value: string; label: string; packageType?: string }[]
  >([]);

  useEffect(() => {
    if (opened) {
      if (editingReward) {
        setFormData({
          name: editingReward.name || "",
          reward_category:
            editingReward.reward_category || "membership_extension",
          value: editingReward.value || 0,
          product_name: editingReward.product_name || "",
          product_id: editingReward.product_id || "",
          allowed_class_id: editingReward.allowed_class_id || "",
          allowed_class_name: editingReward.allowed_class_name || "",
          target_package_id: editingReward.target_package_id || "",
          target_package_name: editingReward.target_package_name || "",
          expiry_days: editingReward.expiry_days || 0,
          branch_ids:
            (editingReward as any).branch_ids ||
            ((editingReward as any).branch_id
              ? [(editingReward as any).branch_id]
              : []),
        });
      } else {
        setFormData({
          name: "",
          reward_category: "membership_extension",
          value: 0,
          product_name: "",
          product_id: "",
          allowed_class_id: "",
          allowed_class_name: "",
          target_package_id: "",
          target_package_name: "",
          expiry_days: 0,
          branch_ids: [],
        });
      }
    }
  }, [opened, editingReward, branchesList]);

  const fetchPackages = async () => {
    if (!companyId) return;
    try {
      const packagesQuery = query(
        collection(db, "packages"),
        where("company_id", "==", companyId),
      );
      const packagesSnap = await getDocs(packagesQuery);
      setPackages(
        packagesSnap.docs
          .filter((d) => d.data().type !== "trial")
          .map((d) => {
            const data = d.data();
            return {
              value: d.id,
              label:
                data.packageName ||
                data.name ||
                data.title ||
                "Unknown Package",
              packageType: data.type,
            };
          }),
      );
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  const fetchClasses = async (branchId: string) => {
    if (!companyId || !branchId) return;
    try {
      const q = query(
        collection(db, "companyprofile", companyId, "classes"),
        where("type", "==", "class_template"),
      );
      const snap = await getDocs(q);
      const allClasses = snap.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as any,
      );

      let branchClasses = allClasses;
      if (branchId !== "all_branches") {
        branchClasses = allClasses.filter((c) => {
          if (!c.class_branch_id) return false;
          if (Array.isArray(c.class_branch_id)) {
            return c.class_branch_id.includes(branchId);
          }
          return c.class_branch_id === branchId;
        });
      }

      setClasses(
        branchClasses.map((d) => ({
          value: d.id,
          label: d.class_name,
        })),
      );
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchProducts = async (branchId: string) => {
    if (!companyId || !branchId) return;
    try {
      let q;
      if (branchId === "all_branches") {
        q = query(
          collection(db, "companyprofile", companyId, "products"),
          where("company_id", "==", companyId),
        );
      } else {
        q = query(
          collection(db, "companyprofile", companyId, "products"),
          where("branch_id", "array-contains", branchId),
        );
      }
      const snap = await getDocs(q);
      setProducts(
        snap.docs.map((d) => ({ value: d.id, label: d.data().name })),
      );
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    const primaryBranch = formData.branch_ids[0];
    if (primaryBranch) {
      fetchClasses(primaryBranch);
      fetchProducts(primaryBranch);
      fetchPackages();
    } else {
      setClasses([]);
      setProducts([]);
    }
  }, [formData.branch_ids]);

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Please enter a reward name");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        company_id: companyId,
        name: formData.name,
        reward_category: formData.reward_category,
        value: formData.value,
        product_name: formData.product_name,
        product_id: formData.product_id,
        allowed_class_id: formData.allowed_class_id,
        allowed_class_name: formData.allowed_class_name,
        target_package_id: formData.target_package_id,
        target_package_name: formData.target_package_name,
        expiry_days: formData.expiry_days,
        branch_ids: formData.branch_ids,
      };

      if (editingReward) {
        await updateDoc(
          doc(db, "companyprofile", companyId, "rewards", editingReward.id),
          {
            ...payload,
            updated_at: serverTimestamp(),
          },
        );
        toast.success("Reward updated successfully");
      } else {
        await addDoc(collection(db, "companyprofile", companyId, "rewards"), {
          ...payload,
          created_at: serverTimestamp(),
        });
        toast.success("Reward created successfully");
      }

      onSaveSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving reward:", error);
      toast.error("Failed to save reward");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editingReward ? "Edit Reward" : "Create Reward"}
      size="xl"
      padding={"lg"}
      withinPortal
      styles={{
        title: {
          fontWeight: "bold",
          height: "10px",
        },
      }}
    >
      <Stack>
        <MultiSelect
          label="Valid Branches"
          placeholder="Select one or more branches this reward applies to"
          description={
            formData.branch_ids.length > 1 ? (
              <Text size="xs" color="dimmed">
                Products & classes shown for the first selected branch.
              </Text>
            ) : undefined
          }
          data={
            branchesList?.map((b: any) => ({
              value: b.id,
              label: b.name,
            })) || []
          }
          value={formData.branch_ids}
          onChange={(vals) =>
            setFormData({
              ...formData,
              branch_ids: vals,
              product_name: "",
              product_id: "",
              allowed_class_id: "",
              allowed_class_name: "",
              target_package_id: "",
              target_package_name: "",
            })
          }
          required
        />

        <TextInput
          label="Reward Name"
          placeholder="e.g. Free Protein Shake, 1 Week Extra"
          value={formData.name}
          onChange={(e) =>
            setFormData({ ...formData, name: e.currentTarget.value })
          }
          required
        />

        <Select
          label="Reward Category"
          data={[
            { value: "membership_extension", label: "Membership Extension" },
            { value: "free_product", label: "Free Product" },
            { value: "free_classes", label: "Free Classes" },
            {
              value: "discount_membership_package",
              label: "Discount on Membership Package",
            },
            {
              value: "discount_class_package",
              label: "Discount on Class Package",
            },
          ]}
          value={formData.reward_category}
          onChange={(val) =>
            setFormData({
              ...formData,
              reward_category: val || "membership_extension",
            })
          }
          required
          withinPortal
        />

        {formData.reward_category === "membership_extension" && (
          <NumberInput
            label="Days to Extend"
            placeholder="e.g. 7"
            value={formData.value}
            onChange={(val) =>
              setFormData({ ...formData, value: Number(val) || 0 })
            }
            required
            min={1}
          />
        )}

        {formData.reward_category === "free_product" && (
          <>
            <Select
              label="Product"
              withAsterisk
              description="Select a product from inventory"
              placeholder="Select a product"
              data={products}
              value={formData.product_id}
              onChange={(val) => {
                const pName =
                  products.find((p) => p.value === val)?.label || "";
                setFormData({
                  ...formData,
                  product_id: val || "",
                  product_name: pName,
                });
              }}
              disabled={formData.branch_ids.length === 0}
            />
            <NumberInput
              label="Quantity"
              value={formData.value}
              onChange={(val) =>
                setFormData({ ...formData, value: Number(val) || 1 })
              }
              required
              min={1}
            />
            <NumberInput
              label="Expiry Days"
              description="How many days after claiming until the voucher expires?"
              value={formData.expiry_days}
              onChange={(val) =>
                setFormData({ ...formData, expiry_days: Number(val) || 0 })
              }
              min={0}
            />
          </>
        )}

        {formData.reward_category === "free_classes" && (
          <>
            <NumberInput
              label="Number of Free Classes"
              placeholder="e.g. 2"
              value={formData.value}
              onChange={(val) =>
                setFormData({ ...formData, value: Number(val) || 0 })
              }
              required
              min={1}
            />
            <Select
              label="Allowed Class"
              placeholder="Select a class"
              data={classes}
              value={formData.allowed_class_id}
              onChange={(val) => {
                const selectedClass = classes.find((c) => c.value === val);
                setFormData({
                  ...formData,
                  allowed_class_id: val || "",
                  allowed_class_name: selectedClass?.label || "",
                });
              }}
              required
            />
            <NumberInput
              label="Expiry Days"
              description="How many days after claiming until the voucher expires?"
              value={formData.expiry_days}
              onChange={(val) =>
                setFormData({ ...formData, expiry_days: Number(val) || 0 })
              }
              min={0}
            />
          </>
        )}

        {(formData.reward_category === "discount_membership_package" ||
          formData.reward_category === "discount_class_package") && (
          <>
            <NumberInput
              label="Discount Percentage (%)"
              placeholder="e.g. 10"
              value={formData.value}
              onChange={(val) =>
                setFormData({ ...formData, value: Number(val) || 0 })
              }
              required
              min={1}
              max={100}
            />
            <Select
              label={
                formData.reward_category === "discount_membership_package"
                  ? "Target Membership Package"
                  : "Target Class Package"
              }
              placeholder="Select a package"
              data={packages.filter((p) =>
                formData.reward_category === "discount_membership_package"
                  ? p.packageType === "scheduled" || p.packageType === "custom"
                  : p.packageType === "class_package",
              )}
              value={formData.target_package_id}
              onChange={(val) => {
                const selectedPkg = packages.find((p) => p.value === val);
                setFormData({
                  ...formData,
                  target_package_id: val || "",
                  target_package_name: selectedPkg?.label || "",
                });
              }}
              required
            />
            <NumberInput
              label="Expiry Days"
              description="How many days after claiming until the voucher expires?"
              value={formData.expiry_days}
              onChange={(val) =>
                setFormData({ ...formData, expiry_days: Number(val) || 0 })
              }
              min={0}
            />
          </>
        )}

        <Group spacing="flex-end" mt="md">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} mx="lg">
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
